import { PathType } from '@repo/api-gw-sdk';

import { stripDriveFileId } from '@/utils/path';

interface Microsoft365RestoreWorkload {
  label: string;
  wholeEntityLabel: string;
  /**
   * Number of namespace segments addressing the entity itself: users/<user>/drive,
   * users/<user>/exchange, sharepoint/<siteId>/drives/<driveId>, teams/<teamId>,
   * teams/<teamId>/channels/<channelId>.
   */
  entityRootDepth: number;
  /** Replaces the File/Folder kind shown after an item's name when the namespace holds something else. */
  itemKind?: string;
}

type Microsoft365RestoreWorkloadName =
  'user' | 'onedrive' | 'exchange' | 'sharepoint' | 'team' | 'teams';

const workloads: Record<
  Microsoft365RestoreWorkloadName,
  Microsoft365RestoreWorkload
> = {
  user: {
    label: 'User',
    wholeEntityLabel: 'Full user',
    entityRootDepth: 2,
  },
  onedrive: {
    label: 'OneDrive',
    wholeEntityLabel: 'Entire OneDrive',
    entityRootDepth: 3,
  },
  exchange: {
    label: 'Exchange',
    wholeEntityLabel: 'Entire mailbox',
    entityRootDepth: 3,
    itemKind: 'Email ID',
  },
  sharepoint: {
    label: 'SharePoint',
    wholeEntityLabel: 'Entire library',
    entityRootDepth: 4,
  },
  team: {
    label: 'Teams',
    wholeEntityLabel: 'Entire Team',
    entityRootDepth: 2,
  },
  teams: {
    label: 'Teams',
    wholeEntityLabel: 'Entire channel',
    entityRootDepth: 4,
    itemKind: 'Message ID',
  },
};

export interface Microsoft365RestoreItem {
  workload: Microsoft365RestoreWorkload;
  /** Raw id of the user, site or Team holding the item; index paths name entities by id only. */
  entityId: string;
  /** The owning user's id, when the item lives under a user rather than a SharePoint site. */
  userId?: string;
  /** The channel's Graph id, when the item lives in a Teams channel. */
  channelId?: string;
  item: string;
}

// The index keeps OneDrive under `drive`; a user root, one level above the workloads, is a whole-user
// restore, and a Team root, one level above its channels, is a whole-Team restore.
const workloadNameFromSegments = (
  segments: string[]
): Microsoft365RestoreWorkloadName | undefined => {
  if (segments[0] === 'users') {
    if (segments.length === 2) {
      return 'user';
    }
    switch (segments[2]) {
      case 'drive':
        return 'onedrive';
      case 'exchange':
        return 'exchange';
    }
    return undefined;
  }
  if (segments[0] === 'sharepoint' && segments.length > 1) {
    return 'sharepoint';
  }
  if (segments[0] === 'teams' && segments.length === 2) {
    return 'team';
  }
  if (segments[0] === 'teams' && segments.length > 2) {
    return 'teams';
  }
  return undefined;
};

/**
 * A Microsoft 365 restore records the search-index key of every selected entry — a workload
 * namespace, the owning user or site, and for files a path carrying a ";fileId" suffix — so what the
 * job view tells about the restore is read back out of that path. A path addressing an entity root
 * rather than something below it is a whole-user, whole-OneDrive, whole-mailbox, whole-library,
 * whole-Team or whole-channel restore.
 */
export const parseMicrosoft365RestoreItem = (
  path: string,
  pathType: PathType
): Microsoft365RestoreItem | undefined => {
  const segments = path.split('/').filter(Boolean);
  const workloadName = workloadNameFromSegments(segments);
  if (!workloadName) {
    return undefined;
  }

  const workload = workloads[workloadName];
  const name = segments.slice(workload.entityRootDepth).at(-1);
  const kind =
    workload.itemKind ?? (pathType === PathType.File ? 'File' : 'Folder');

  return {
    workload,
    entityId: segments[1],
    userId: segments[0] === 'users' ? segments[1] : undefined,
    channelId: workloadName === 'teams' ? segments[3] : undefined,
    item: name
      ? `${stripDriveFileId(name)} (${kind})`
      : workload.wholeEntityLabel,
  };
};

// A user restore selects workloads, so what it covered is read off the recorded workloads, not the path.
export const isWholeUserItem = (item: Microsoft365RestoreItem): boolean =>
  item.workload === workloads.user;

// A Teams item is owned by its Team whether it addresses the whole Team or one channel of it.
export const isTeamsItem = (item: Microsoft365RestoreItem): boolean =>
  item.workload === workloads.team || item.workload === workloads.teams;

export const microsoft365RestoreSourceLabel = (
  item: Microsoft365RestoreItem,
  entityName?: string
): string => [item.workload.label, entityName || item.entityId].join(' · ');

/**
 * What the row names as the restored item. A whole-channel restore's path ends at the channel id,
 * which says nothing to a reader, so the channel's display name leads when the directory knows it.
 */
export const microsoft365RestoreItemLabel = (
  item: Microsoft365RestoreItem,
  channelName?: string
): string =>
  item.channelId && channelName && item.item === item.workload.wholeEntityLabel
    ? `${channelName} (${item.workload.wholeEntityLabel})`
    : item.item;

/**
 * The human-readable form of a Microsoft 365 index path: the ";<fileId>" suffix the index keeps on
 * file segments is dropped, and the id segments of the owning entity (user, site or Team) and of a
 * Teams channel give way to their display names when known.
 */
export const microsoft365DisplayPath = (
  path: string,
  entityName?: string,
  channelName?: string
): string => {
  const segments = stripDriveFileId(path).split('/');
  if (entityName && segments.length > 1) {
    segments[1] = entityName;
  }
  if (channelName && segments[0] === 'teams' && segments.length > 3) {
    segments[3] = channelName;
  }
  return segments.join('/');
};

// The Microsoft 365 explorer selects users and workloads from component state rather than the URL, so
// the closest a link can land is the tenant itself.
export const microsoft365ExplorerHref = (resourceId: string): string =>
  `/saas-inventory/microsoft-365/${encodeURIComponent(resourceId)}`;
